import { useMemo } from 'react';
import { Column } from 'react-table';

import CheckboxDefault from 'app/components/Checkbox/CheckboxDefault';
import { ActionsCell } from 'app/components/Table/columns/ActionsCell';
import { CustomHeader } from 'app/components/Table/columns/CustomHeader';
import { ActionTable, ID, TYPE_MODAL } from 'app/components/Table/core/types';
import Typography from 'app/components/Typography';

import { useAuth } from 'app/modules/auth';
import { SensorStatus } from 'app/modules/inventory/bins/components/SensorStatus';
import { SensorConnectionStatus, SensorSkuId } from 'app/modules/inventory/bins/core/enum';
import { Bin } from 'app/modules/inventory/bins/core/type';
import { Rack } from 'app/modules/inventory/racks/core/type';
import { getFormatedDate } from 'app/modules/reporting/core/helpers';
import ExportHeartbeat from 'app/modules/system-settings/devices/components/ExportHeartbeat';
import ExportLedger from 'app/modules/system-settings/devices/components/ExportLedger';
import ExportMQTT from 'app/modules/system-settings/devices/components/ExportMQTT';
import { DYNAMIC_WIDTH } from 'app/modules/system-settings/devices/core/constant';

import { formatCustomDate } from 'app/helpers';
import { downloadFile, downloadImage } from 'app/helpers/utils';
import { usePolicyInUser } from 'app/hooks/policy';

import { PATH } from 'app/constants/path';

import { ReactComponent as VideoIcon } from 'app/assets/icons/video-icon.svg';

import StatusDeviceCard from '../components/StatusDeviceCard';
import { DeviceType } from './enum';
import { Device, Sensor } from './type';

export const devicesColumns = (typeSub: DeviceType): ReadonlyArray<Column<Device>> => {
  const actions = (devices: Device) => {
    const listActions: ActionTable[] = [
      {
        name: 'Edit',
        type: TYPE_MODAL.EDIT,
      },
    ];
    listActions.push({
      name: devices.linked ? 'Generate new code' : 'Connect tablet',
      type: devices.linked ? TYPE_MODAL.GENERATE_CODE : TYPE_MODAL.CONNECT_TABLET,
    });
    listActions.push({
      name: 'Delete',
      type: TYPE_MODAL.DELETE,
    });
    return listActions;
  };
  return [
    {
      id: 'createdAt',
      accessor: 'createdAt',
      Header: (props) => <CustomHeader tableProps={props} title="Created at" />,
      width: '20%',
      Cell: ({ ...props }) => (
        <Typography style={{ width: '11ch' }} variant="body-5" color="neutral-10">
          {formatCustomDate(props.data[props.row.index].createdAt)}
        </Typography>
      ),
    },
    {
      id: 'name',
      accessor: 'name',
      width: '35%',
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          title={typeSub === DeviceType.tablet ? 'Tablet' : 'Gateway'}
        />
      ),
    },

    {
      id: 'wardName',
      accessor: 'inventoryLocation',
      Header: (props) => <CustomHeader tableProps={props} title="Location" />,
      Cell: ({ ...props }) => (
        <Typography
          variant="body-5"
          color="neutral-10"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {props.data[props.row.index].inventoryLocation?.location?.name}{' '}
        </Typography>
      ),
      width: '20%',
    },
    {
      id: 'status',
      Header: (props) => <CustomHeader tableProps={props} title="Status" />,
      Cell: ({ ...props }) => <StatusDeviceCard device={props.data[props.row.index]} />,
      width: '20%',
    },
    {
      id: 'actions',
      width: '5%',
      Header: (props) => (
        <CustomHeader tableProps={props} title="" className="text-end min-w-100px" />
      ),
      Cell: ({ ...props }) => (
        <ActionsCell
          item={props.data[props.row.index]}
          actions={actions(props.data[props.row.index])}
        />
      ),
    },
  ];
};

export const sensorsColumns = (
  onDownloadExpirySensor: (binId: ID) => () => void,
  onShowUpdateBaseWeightModal: (sensor: Sensor) => () => void,
  isSystemAdminCurrent: boolean,
): ReadonlyArray<Column<Sensor>> => {
  const actions = (device: Sensor) => {
    const listActions: ActionTable[] = [
      {
        name: 'Edit',
        type: TYPE_MODAL.EDIT,
      },
    ];
    listActions.push({
      name: 'Delete',
      type: TYPE_MODAL.DELETE,
    });
    listActions.push({
      name: 'Visual Archive',
      type: TYPE_MODAL.NONE,
      redirect: PATH.SYSTEM_SETTING_DEVICE_SENSOR_DETAIL?.replace(':name', device.name || ''),
    });
    if (device.inventoryLocation?.rack?.id) {
      listActions.push({
        name: 'Go to Rack View',
        type: TYPE_MODAL.NONE,
        redirect: PATH.RACK_DETAIL?.replace(
          ':rackId',
          device.inventoryLocation?.rack?.id?.toString() || '',
        ),
      });
    }
    if (device.skuId === SensorSkuId.Weight) {
      listActions.push({
        name: 'Update Base Weight',
        type: TYPE_MODAL.NONE,
        onClick: onShowUpdateBaseWeightModal(device),
      });
    }
    if (isSystemAdminCurrent) {
      listActions.push({
        name: 'Export MQTT',
        type: TYPE_MODAL.NONE,
        content: <ExportMQTT sensorId={device.id} />,
      });
      listActions.push({
        name: 'Export Ledger',
        type: TYPE_MODAL.NONE,
        content: <ExportLedger sensorId={device.id} />,
      });
      listActions.push({
        name: 'Export Heartbeat',
        type: TYPE_MODAL.NONE,
        content: <ExportHeartbeat code={device.code} />,
      });
      if (device.skuId === SensorSkuId.Weight) {
        listActions.push({
          name: 'Export Heartbeat + 10Hz',
          type: TYPE_MODAL.NONE,
          content: <ExportHeartbeat code={device.code} is10Hz={true} />,
        });
      }
      if (device.skuId === SensorSkuId.Expiry) {
        listActions.push({
          name: 'Export Expiry',
          type: TYPE_MODAL.NONE,
          onClick: onDownloadExpirySensor(device?.code),
        });
      }
    }
    return listActions;
  };

  const renderBinAndRack = (
    inventoryLocation?: { bin: Bin; rack: Rack },
    type: 'bin' | 'rack' = 'bin',
  ) => {
    const { bin, rack } = inventoryLocation ?? {};

    if (bin || rack) return type === 'bin' ? bin?.name : rack?.name;
    return 'Unassigned';
  };

  return [
    {
      id: 'createdAt',
      accessor: 'createdAt',
      Header: (props) => <CustomHeader tableProps={props} title="Created at" />,
      width: '10%',
      Cell: ({ ...props }) => (
        <Typography style={{ width: '11ch' }} variant="body-5" color="neutral-10">
          {formatCustomDate(props.data[props.row.index].createdAt)}
        </Typography>
      ),
    },
    {
      id: 'piId',
      accessor: 'piId',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Edge ID'} />,
    },
    {
      id: 'cameraId',
      accessor: 'cameraId',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Camera ID'} />,
    },
    {
      id: 'viewId',
      accessor: 'viewId',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'View ID'} />,
    },
    {
      id: 'hangerId',
      accessor: 'hangerId',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Hanger ID'} />,
    },
    {
      id: 'baseWeight',
      accessor: 'baseWeight',
      width: `${DYNAMIC_WIDTH}%`,
      Header: 'Base Weight',
      Cell: ({ value }) => (
        <Typography variant="body-5" color="neutral-10">
          {value}
        </Typography>
      ),
    },
    {
      id: 'bin',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Bin'} />,
      Cell: ({ ...props }) => (
        <Typography variant="body-5" color="neutral-10">
          {renderBinAndRack(props.data[props.row.index]?.inventoryLocation)}
        </Typography>
      ),
    },
    {
      id: 'rack',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Rack'} />,
      Cell: ({ ...props }) => (
        <Typography variant="body-5" color="neutral-10">
          {renderBinAndRack(props.data[props.row.index]?.inventoryLocation, 'rack')}
        </Typography>
      ),
    },
    {
      id: 'code',
      accessor: 'code',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Sensor Address'} />,
    },
    {
      id: 'type',
      accessor: 'skuId',
      width: `0%`,
      Header: (props) => <CustomHeader tableProps={props} title={'Type'} />,
      Cell: ({ ...props }) => {
        const skuId = props.data[props.row.index].skuId;
        const { bin, rack } = props.data[props.row.index]?.inventoryLocation ?? {};

        return (
          <SensorStatus
            status={
              !bin || !rack
                ? SensorConnectionStatus.notReady
                : props.data[props.row.index].heartbeatStatus
            }
            skuId={skuId}
          />
          // <div className="d-flex align-items-center gap-3">
          //   <SensorStatus
          //     status={
          //       !bin || !rack
          //         ? SensorConnectionStatus.notReady
          //         : props.data[props.row.index].heartbeatStatus
          //     }
          //     skuId={skuId}
          //   />
          //   <Typography variant="body-5" color="neutral-10" style={{ whiteSpace: 'nowrap' }}>
          //     {skuId === 'Weight' ? 'Weight' : 'Computer vision'}
          //   </Typography>
          // </div>
        );
      },
    },
    {
      id: 'wardName',
      accessor: 'inventoryLocation',
      width: `${DYNAMIC_WIDTH}%`,
      Header: (props) => <CustomHeader tableProps={props} title="Location" />,
      Cell: ({ ...props }) => (
        <Typography variant="body-5" color="neutral-10" style={{ whiteSpace: 'nowrap' }}>
          {props.data[props.row.index].inventoryLocation?.location?.name}
        </Typography>
      ),
    },
    // {
    //   id: 'heartBeat',
    //   width: '9.44%',
    //   Header: (props) => <CustomHeader tableProps={props} title="Connection status" />,
    //   Cell: ({ ...props }) => <StatusDefault label={'Connected'} isActive={true} />,
    // },
    {
      id: 'actions',
      width: '5%',
      Header: (props) => (
        <CustomHeader tableProps={props} title="" className="text-end min-w-100px" />
      ),
      Cell: ({ ...props }) => (
        <ActionsCell
          item={props.data[props.row.index]}
          actions={actions(props.data[props.row.index])}
        />
      ),
    },
  ];
};

export const visualArchiveColumns = (
  selectedItems: string[],
  handleSelectItem: (itemId: string) => void,
): ReadonlyArray<Column<any>> => {
  const actions = (data: any) => {
    const listActions: ActionTable[] = [
      {
        name: 'Download',
        type: TYPE_MODAL.NONE,
        onClick: () => {
          const url = data.url;
          const filename = `File-${getFormatedDate(new Date())}`;

          if (url) {
            data.type === 'image' ? downloadImage(url, filename) : downloadFile(url, filename);
          }
        },
      },
    ];

    return listActions;
  };

  return [
    {
      id: 'checkbox',
      width: `0%`,
      Header: (props) => <CustomHeader tableProps={props} isSortable={false} title="" />,
      Cell: ({ ...props }) => {
        const itemId = props.data[props.row.index].id || props.row.id;
        const isChecked = selectedItems.includes(itemId);

        const checkboxChange = () => handleSelectItem(itemId);

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <CheckboxDefault checked={isChecked} onChange={checkboxChange} />
          </div>
        );
      },
    },
    {
      id: 'createdAt',
      accessor: 'createdAt',
      width: `10%`,
      Header: (props) => <CustomHeader tableProps={props} title="Created at" />,
      Cell: ({ ...props }) => {
        const createdAt = props.data[props.row.index].createdAt;
        return (
          <Typography variant="body-5" color="neutral-10">
            {formatCustomDate(createdAt)}
          </Typography>
        );
      },
    },
    {
      id: 'fileName',
      accessor: 'fileName',
      width: `75%`,
      Header: (props) => <CustomHeader tableProps={props} title="Name" />,
      Cell: ({ ...props }) => {
        const filePath = props.data[props.row.index].name;
        const name = filePath.split('/').pop();
        return (
          <figure className="m-0 d-flex align-items-center gap-4">
            {props.data[props.row.index].type === 'image' ? (
              <img
                width={56}
                height={56}
                loading="lazy"
                className="rounded-2"
                src={props.data[props.row.index].url}
                alt={props.data[props.row.index].name}
              />
            ) : (
              <VideoIcon className="rounded-2" />
            )}
            <Typography variant="body-5" color="neutral-10">
              {name}
            </Typography>
          </figure>
        );
      },
    },
    {
      id: 'fileType',
      accessor: 'fileType',
      width: `10%`,
      Header: (props) => <CustomHeader tableProps={props} title="Type" />,
      Cell: ({ ...props }) => {
        const fileType = props.data[props.row.index].type;
        return (
          <Typography variant="body-5" color="neutral-10">
            {fileType}
          </Typography>
        );
      },
    },
    {
      id: 'actions',
      width: '5%',
      Header: (props) => (
        <CustomHeader tableProps={props} title="" className="text-end min-w-100px" />
      ),
      Cell: ({ ...props }) => (
        <ActionsCell
          item={props.data[props.row.index]}
          actions={actions(props.data[props.row.index])}
          dividerIndexes={[2]}
        />
      ),
    },
  ];
};
